import { create } from 'zustand';

/**
 * Zustand store for caching select options across FormFactory components
 * 
 * This store maintains a cache of select options loaded from the API to avoid
 * redundant requests. Options are stored using keys in the format: {tableName}.{columnId}[.filtersHash]
 * where filtersHash is optional and only added when filters are present.
 */
const useSelectOptionsStore = create((set, get) => ({
  // State - stores cached options by key
  cachedOptions: {},
  
  // Generate a cache key from tableName, columnId, and optional filters
  generateCacheKey: (tableName, columnId, filters = null) => {
    let key = `${tableName}.${columnId}`;
    
    // If filters exist, create a hash to append to the key
    if (filters && Array.isArray(filters) && filters.length > 0) {
      // Sort filters to ensure consistent hashing regardless of order
      const sortedFilters = [...filters].sort((a, b) => {
        return a.columnId.localeCompare(b.columnId);
      });
      
      // Create a simple hash from the filters
      const filtersString = JSON.stringify(sortedFilters);
      const filtersHash = filtersString.split('').reduce((hash, char) => {
        const charCode = char.charCodeAt(0);
        return ((hash << 5) - hash) + charCode;
      }, 0);
      
      key += `.${Math.abs(filtersHash)}`;
    }
    
    return key;
  },
  
  // Get cached options for a specific table, column, and optional filters
  getOptions: (tableName, columnId, filters = null) => {
    const key = get().generateCacheKey(tableName, columnId, filters);
    const options = get().cachedOptions[key];
    
    if (options) {
      console.log(`[SelectOptionsStore] Loading options from cache for key: ${key}`);
      return options;
    }
    
    console.log(`[SelectOptionsStore] No cached options found for key: ${key}`);
    return null;
  },
  
  // Set cached options for a specific table, column, and optional filters
  setOptions: (tableName, columnId, options, filters = null) => {
    const key = get().generateCacheKey(tableName, columnId, filters);
    
    set((state) => ({
      cachedOptions: {
        ...state.cachedOptions,
        [key]: options
      }
    }));
    
    console.log(`[SelectOptionsStore] Cached options for key: ${key}`);
  },
  
  // Delete cached options - accepts single object or array of objects
  deleteOptions: (optionsToDelete) => {
    // Normalize to array
    const optionsArray = Array.isArray(optionsToDelete) ? optionsToDelete : [optionsToDelete];
    
    set((state) => {
      const newCachedOptions = { ...state.cachedOptions };
      const deletedKeys = [];
      
      optionsArray.forEach(option => {
        const key = get().generateCacheKey(option.tableName, option.columnId, option.filters);
        if (newCachedOptions[key]) {
          delete newCachedOptions[key];
          deletedKeys.push(key);
        }
      });
      
      if (deletedKeys.length > 0) {
        console.log(`[SelectOptionsStore] Deleted cached options for keys:`, deletedKeys);
      }
      
      return { cachedOptions: newCachedOptions };
    });
  },
  
  // Delete all cached options
  clearAllOptions: () => {
    set({ cachedOptions: {} });
    console.log(`[SelectOptionsStore] Cleared all cached options`);
  },
  
  // Get all cached option keys
  getCachedKeys: () => {
    return Object.keys(get().cachedOptions);
  },
  
  // Reset the entire store (used on logout)
  resetStore: () => {
    set({ cachedOptions: {} });
    console.log('[SelectOptionsStore] Store reset on logout');
  },
  
  // Get cache statistics
  getCacheStats: () => {
    const cachedOptions = get().cachedOptions;
    const keys = Object.keys(cachedOptions);
    const totalItems = keys.reduce((sum, key) => {
      return sum + (Array.isArray(cachedOptions[key]) ? cachedOptions[key].length : 0);
    }, 0);
    
    return {
      totalCachedLists: keys.length,
      totalCachedItems: totalItems,
      cachedKeys: keys
    };
  }
}));

// Export utility functions for external cache management
export const selectOptionsStoreUtils = {
  // Clear specific options
  clearOptions: (tableName, columnId, filters = null) => {
    useSelectOptionsStore.getState().deleteOptions({ tableName, columnId, filters });
  },
  
  // Clear multiple options by array of {tableName, columnId, filters} objects
  clearMultipleOptions: (optionsArray) => {
    useSelectOptionsStore.getState().deleteOptions(optionsArray);
  },
  
  // Clear all cached options
  clearAllOptions: () => {
    useSelectOptionsStore.getState().clearAllOptions();
  },
  
  // Get cache statistics
  getCacheStats: () => {
    return useSelectOptionsStore.getState().getCacheStats();
  },
  
  // Get all cached keys
  getCachedKeys: () => {
    return useSelectOptionsStore.getState().getCachedKeys();
  },
  
  // Reset the entire store
  resetStore: () => {
    useSelectOptionsStore.getState().resetStore();
  }
};

export default useSelectOptionsStore;
